import { IAvatar } from './avatar';
import { IMap } from './map';
import { IParticipant } from './participants';
import { IRoom } from './rooms';
import { IStreamService } from './streamService';
import { IMillicastEventTokens } from './millicastTokens';
import { WsConnectionClientType } from './index';
import { IEventParticipant } from './eventParticipant';
import { IMytaverseFile } from './file';
import { IWorld } from './worlds';
import { StreamingProviders } from '@core/dashboard/providers/MytaverseEventProvider/interfaces';

export enum EventStatus {
  SERVER_INITIALIZATION = 'SERVER_INITIALIZATION',
  TESTING = 'TESTING',
  SERVER_SCALE_DOWN = 'SERVER_SCALE_DOWN',
  SCHEDULED = 'SCHEDULED',
  SERVER_SCALE_UP = 'SERVER_SCALE_UP',
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  FINISHED = 'FINISHED',
  SUSPENDED = 'SUSPENDED',
  DELETED = 'DELETED',
  ERROR = 'ERROR',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
}

export interface IEventACL {
  path: string;
  value: string;
  disabled: boolean;
}

export interface IIdleTimerSettings {
  timeout?: number; // seconds
  counterTimeout?: number; // seconds
  enabled?: boolean;
}

export enum PollyLanguage {
  ENGLISH = 'en',
  FRENCH = 'fr',
  GERMAN = 'de',
}

export interface IEventSettingsAiAssistant {
  engine: string;
  voiceId: string;
  language: PollyLanguage;
}

export interface IEventSettings {
  //AI
  aiEnabled?: boolean; //enableAssistant
  aiVirtualAssistantName?: string; //todo: apply where necessary
  aiAssistant?: IEventSettingsAiAssistant;

  //Timers
  timerIdleSettings?: IIdleTimerSettings; //idleTimer
  timerLoadingTimeMins?: number;

  //General
  generalDisabledKeys?: string; //todo: apply where necessary

  //Server
  serverSuspendAfterPublish?: boolean; //suspendAfterPublish
}

export interface IEvent {
  eventId: string;
  customUrl: string;
  endTime: number;
  image: string;
  name: string;
  startTime: number;
  streamServiceId: string;
  user: string;
  eventReleaseId?: string;
  logoImage: IMytaverseFile;
  agendaImage: string;
  participants: IParticipant[];
  eventAvatars: IAvatar[];
  rooms: IRoom[];
  maps: IMap[];
  clientChangeset?: string;
  avatars: IAvatar[];
  streamService?: IStreamService;
  dolbyConferenceId: string;
  region?: string;
  lastJoinTime?: number;
  eventRelease: IEventRelease;
  role: string;
  loadingScreenAsset?: IMytaverseFile | string | null;
  backgroundImage?: IMytaverseFile;
  worldId: string;
  world?: IWorld | null;
  status: EventStatus;
  eventTags?: string[];
  inviteSlotsTags?: string[];
  //enableAssistant?: boolean; //deprecated
  //enablePureGamecast?: string; //deprecated
  spsApplication?: {
    spsApplicationId: string;
    wsUrl: string;
  };

  externalUrl?: string;
  eventLocation?: string;
  isDisabled?: boolean;
  disabledKeyCommands?: string[];
  isFirstTimeTooltip?: boolean;
  isNotAvailableForGradeCodeTooltip?: boolean;

  accessControlList?: IEventACL[];
  inviteStartTime?: number;
  isScheduled?: boolean;
  isAlwaysOn?: boolean;

  //idleTimer?: IIdleTimerSettings; //deprecate
  //loadingTimeMins?: number; //deprecate

  settings?: IEventSettings;
}

export interface IEventRelease {
  eventReleaseId: string;
  name: string;
  timestamp: number;
  status: EventReleaseStatus;
  eventId: string;
  region: string;
  gameliftEC2InstanceRegion: string;
  stateMachineExecutionArn: string;
  gameliftFleetId?: string;
  gamecastPixelStreamingClientId?: string;
  comment?: string;
  serverChangeset?: string;
  clientChangeset?: string;
  version?: string;
  gamecastLocations?: string[];
  streamClasses: string[];
  pixelStreamingClientIds?: string[];
  pixelStreamingClients?: IPixelStreamingClient[];
  gameliftServersBuildId?: string;
}

export enum EventReleaseStatus {
  INITIALIZATION = 'INITIALIZATION',
  ACTIVE = 'ACTIVE',
  FAILED = 'FAILED',
  DELETED = 'DELETED',
}

export enum PlayerSessionStatus {
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  RESERVED = 'RESERVED',
  TIMEDOUT = 'TIMEDOUT',
}

export interface PlayerSession {
  PlayerSessionId?: string;
  PlayerId?: string;
  GameSessionId?: string;
  FleetId?: string;
  FleetArn?: string;
  CreationTime?: Date;
  TerminationTime?: Date;
  Status?: PlayerSessionStatus;
  IpAddress?: string;
  DnsName?: string;
  Port?: number;
  PlayerData?: string;
}

export interface IEventTokens {
  dolbyToken: string;
  streamChatToken: string;
  millicastTokens: IMillicastEventTokens;
  gamelift: {
    playerSession: {};
  };
}

export enum FollowingStatus {
  Accepted = 'ACCEPTED',
  Pending = 'PENDING',
}

export interface IFollower {
  following: string; // adminId
  eventId: string;
  participantId: string;
  clientType: WsConnectionClientType;
  followingStatus: FollowingStatus;
}

export interface IFollowingDataResponse {
  followers: IFollower[];
  groupLeads: string[];
}

export interface IParticipantEvents {
  events: IEventParticipant[];
  lastKey?: IEventParticipant | null;
}

export interface IPixelStreamingClient {
  clientId: string;
  eventId?: string;
  clientType: StreamingProviders;
  clientVersion: string;
  serverVersion: string;
  timestamp: number;
  clientParams: {
    containerTag: string;
    name: string;

    additionalLaunchArgs?: string;
  };
  coreweaveClientData?: {
    spsApplicationId?: string;
    spsVersionId?: string;
    spsWSEndpoint?: string;
  };

  gamecastClientData?: IGamecastClientData;
}

export interface IGamecastClientData {
  streamGroupId?: string | null;
  applicationId?: string;
  region?: string;
  additionalLaunchArgs?: string[];

  buildUri?: string;
  description?: string;
  streamClass?: string;
  tags?: string[];
  clientToken?: string;
}

export interface IGetStreamingProviderResponse {
  streamingProvider: StreamingProviders | null;
  pixelStreamingClient: IPixelStreamingClient;
}

export interface IParticipantAvatar {
  isAvatarTermsAccepted?: boolean | null;
  customAvatarUrl?: string;
  avatarId?: string;
}
